import React from 'react';
import { Document, Page, View, Text, Image, pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { formatarData } from '../../../contents/VariaveisGlobais';
import empresasData from '../../../../lib/json/empresas.json';
import { styles } from './styles';

// Mapa comparativo para PDF. Mantém a assinatura utilizada pelo index.js da cotação.
// A4 paisagem: Produto (36%), Quantidade (12%) e até dois fornecedores (26% cada).
const FORNECEDORES_POR_GRUPO = 2;
const RESUMO_OBSERVACAO = 115;
const MAX_LINHAS_ANEXO = 31;
const COLUNA_PRODUTO = '36%';
const COLUNA_QTD = '12%';
const COLUNA_FORNECEDOR = '26%';

const texto = (valor) => String(valor ?? '').trim();
const linha = (valor) => texto(valor).replace(/\r\n?/g, '\n');
const realNumeroDocumento = (value) => {
  const valueText = texto(value);
  return valueText && valueText !== 'XXXXXX' && valueText !== 'XXXXXXXXXXXXXXX'
    ? valueText : '';
};

const dataFormatada = (valor) => {
  const data = texto(valor);
  if (!data) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(data)) return data;
  try { return formatarData(data) || data; } catch (_) { return data; }
};

const moeda = (valor, casas = 2) => {
  let value = texto(valor);
  if (!value) return '-';
  if (/^R\$/i.test(value)) return value;
  // A rotina normalmente já entrega preços em decimal brasileiro.
  if (/^-?\d+(?:[.,]\d+)?$/.test(value) && !value.includes(',')) {
    const num = Number(value);
    if (Number.isFinite(num)) {
      value = num.toLocaleString('pt-BR', {
        minimumFractionDigits: casas,
        maximumFractionDigits: casas,
      });
    }
  }
  return `R$ ${value}`;
};

const numeroPositivo = (valor) => {
  const s = texto(valor).replace(/R\$|\s/g, '');
  const normalizado = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
  return Number(normalizado) > 0;
};

function chunks(lista, tamanho) {
  const resultado = [];
  for (let i = 0; i < lista.length; i += tamanho) resultado.push(lista.slice(i, i + tamanho));
  return resultado;
}

// Mesmo filtro de fornecedores usado no Mapa na tela.
function fornecedoresVisiveis(produtos, cotacaoTela, exibeMapa) {
  const filtros = Array.isArray(exibeMapa) ? exibeMapa.map(x => texto(x?.value)).filter(Boolean) : [];
  const chaves = new Set(filtros);
  const mostrarTodos = filtros.length === 0 || chaves.has('Todos');
  const apenasRespondidos = chaves.has('Respondidos') && !mostrarTodos;
  const lista = new Map();

  produtos.forEach((produto) => {
    (produto.fornecedores || []).forEach((fornecedor) => {
      const chave = texto(fornecedor.chaveFornecedor) || `${texto(fornecedor.cnpj)}|${texto(fornecedor.nome)}`;
      const mostrar = !cotacaoTela || mostrarTodos ||
        (apenasRespondidos && fornecedor.respondido) || (!apenasRespondidos && chaves.has(chave));
      if (!mostrar) return;

      if (!lista.has(chave)) {
        lista.set(chave, {
          chave,
          nome: texto(fornecedor.nome),
          cnpj: texto(fornecedor.cnpj),
          respondido: Boolean(fornecedor.respondido),
          tipoFrete: fornecedor.tipoFrete,
          frete: fornecedor.frete,
          totalFornecedor: fornecedor.totalFornecedor,
          totalSelecionado: fornecedor.totalSelecionado,
          condicaoPagamento: fornecedor.condicaoPagamento,
          dataValidade: fornecedor.dataValidade,
          observacaoFornecedor: fornecedor.observacaoFornecedor,
          observacaoMapa: fornecedor.observacaoMapa,
          justificativa: fornecedor.justificativa,
        });
      }
    });
  });
  return Array.from(lista.values());
}

function encontrarResposta(produto, fornecedor) {
  return (produto.fornecedores || []).find((f) =>
    (texto(f.chaveFornecedor) || `${texto(f.cnpj)}|${texto(f.nome)}`) === fornecedor.chave
  );
}

function ofertaSelecionada(produto, supplier) {
  const f = encontrarResposta(produto, supplier);
  return Boolean(f && (f.selecionado || realNumeroDocumento(f.numeroPedido) || realNumeroDocumento(f.numeroContrato)));
}

function resumoImpostos(fornecedor) {
  const campos = [
    ['ICMS', fornecedor.valorICMS],
    ['IPI', fornecedor.valorIPI],
    ['ICMS-ST', fornecedor.valorICMSST],
    ['DIFAL', fornecedor.valorDIFAL],
    ['ISS', fornecedor.valorISS],
  ];
  return campos.filter(([_, valor]) => numeroPositivo(valor))
    .map(([nome, valor]) => `${nome}: ${moeda(valor)}`).join('  |  ');
}

function observacaoCurta(valor) {
  const val = linha(valor).replace(/\s+/g, ' ');
  if (val.length <= RESUMO_OBSERVACAO) return val;
  return `${val.slice(0, RESUMO_OBSERVACAO - 3)}... (ver detalhes)`;
}

// Mantém a linha do produto inteira e reserva espaço para os totais da última página.
function estimativaAltura(produto, fornecedores) {
  const tamanhoProduto = texto(produto.nome).length + texto(produto.descricaoComplementar).length;
  const linhasProduto = Math.ceil(tamanhoProduto / 72);
  let adicional = 0;
  fornecedores.forEach((supplier) => {
    const oferta = encontrarResposta(produto, supplier);
    if (!oferta) return;
    const observacao = Math.min(texto(oferta.observacaoItem).length, RESUMO_OBSERVACAO);
    const linhas = Math.ceil(observacao / 45);
    adicional = Math.max(adicional, Math.max(0, linhas - 1) * 7 + (resumoImpostos(oferta) ? 12 : 0));
  });
  return Math.max(68, 52 + (Math.max(0, linhasProduto - 1) * 8) + adicional);
}

function distribuirProdutos(produtos, fornecedores) {
  const resultado = [];
  let atual = [];
  let altura = 0;
  const ALTURA_LIMITE = 430;
  produtos.forEach((produto) => {
    const peso = estimativaAltura(produto, fornecedores);
    if (atual.length && (atual.length >= 5 || altura + peso > ALTURA_LIMITE)) {
      resultado.push(atual);
      atual = [];
      altura = 0;
    }
    atual.push(produto);
    altura += peso;
  });
  if (atual.length) resultado.push(atual);
  return resultado;
}

const valorOpcao = (valor) => {
  if (typeof valor === 'string') return texto(valor);
  return texto(valor?.label || valor?.text || valor?.value);
};

function tipoFrete(fornecedor) {
  const tipo = valorOpcao(fornecedor.tipoFrete);
  return tipo === 'C' ? 'CIF' : tipo === 'F' ? 'FOB' : (tipo || '-');
}

const Cabecalho = ({ primeiro, empresa, dataHora, cotacaoTela }) => (
  <View style={styles.cabecalho}>
    <View style={styles.cabecalhoSuperior}>
      <View style={styles.logoArea}>
        {cotacaoTela && empresa?.logoEmpresa ? (
          <Image style={styles.logo} src={`${process.env.PUBLIC_URL}/${empresa.logoEmpresa}`} />
        ) : <Text style={styles.logoSubstituto}>MAPA</Text>}
      </View>
      <View style={styles.tituloArea}>
        <Text style={styles.titulo}>MAPA COMPARATIVO DE COTAÇÃO - {texto(primeiro.cotacao)}</Text>
        <Text style={styles.subtitulo}>Comparativo de preços, prazos e condições comerciais</Text>
      </View>
      <View style={styles.dataArea}>
        <Text>Data: {format(dataHora, 'dd/MM/yyyy')}</Text>
        <Text>Hora: {format(dataHora, 'HH:mm:ss')}</Text>
      </View>
    </View>
    <View style={styles.faixaDados}>
      <Text>Comprador: {texto(primeiro.comprador) || '-'}</Text>
      <Text>Emissão: {dataFormatada(primeiro.emissao) || '-'}</Text>
      <Text>Empresa/Filial: {texto(empresa?.empresaFilial) || '-'}</Text>
    </View>
  </View>
);

const Rodape = () => (
  <View fixed style={styles.rodape}>
    <Text>Mapa Comparativo de Cotação</Text>
    <Text render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />
  </View>
);

const CabecalhoTabela = ({ grupo }) => (
  <View style={styles.cabecalhoTabela} wrap={false}>
    <View style={[styles.celulaHeader, { width: COLUNA_PRODUTO }]}>
      <Text style={styles.labelHeader}>PRODUTO</Text>
      <Text style={styles.headerSub}>Código, descrição e centro de custo</Text>
    </View>
    <View style={[styles.celulaHeader, { width: COLUNA_QTD }]}>
      <Text style={styles.labelHeader}>QUANTIDADE</Text>
      <Text style={styles.headerSub}>Necessidade</Text>
    </View>
    {grupo.map((supplier) => (
      <View key={supplier.chave} style={[styles.celulaHeader, { width: COLUNA_FORNECEDOR }]}>
        <Text style={styles.nomeFornecedor}>{supplier.nome || 'FORNECEDOR'}</Text>
        <Text style={styles.headerSub}>{supplier.cnpj || 'CNPJ não informado'}</Text>
      </View>
    ))}
    {grupo.length < 2 && Array.from({ length: 2 - grupo.length }, (_, index) => (
      <View key={`coluna-vazia-${index}`} style={[styles.celulaHeader, { width: COLUNA_FORNECEDOR }]}>
        {grupo.length === 0 && index === 0 ? <Text style={styles.nomeFornecedor}>Nenhum fornecedor no filtro</Text> : null}
      </View>
    ))}
  </View>
);

const DadosOferta = ({ produto, supplier, cotacaoTela }) => {
  const f = encontrarResposta(produto, supplier);
  if (!f) return <View style={styles.celulaOferta}><Text style={styles.semOferta}>Sem proposta para este item</Text></View>;
  const pedido = realNumeroDocumento(f.numeroPedido);
  const contrato = realNumeroDocumento(f.numeroContrato);
  const selecionado = cotacaoTela && (Boolean(f.selecionado) || Boolean(pedido) || Boolean(contrato));
  const impostos = resumoImpostos(f);
  return (
    <View style={styles.celulaOferta}>
      {selecionado && <Text style={styles.indicadorSelecionado}>SELECIONADO</Text>}
      <View style={styles.precoRow}>
        <View style={styles.precoHalf}>
          <Text style={styles.miniLabel}>PREÇO UNITÁRIO</Text>
          <Text style={styles.valorUnitario}>{moeda(f.valor, 4)}</Text>
        </View>
        <View style={styles.precoHalf}>
          <Text style={styles.miniLabel}>TOTAL ITEM</Text>
          <Text style={styles.valorTotal}>{moeda(f.valorTotal)}</Text>
        </View>
      </View>
      <Text style={styles.linhaInformacao}>Entrega: {dataFormatada(f.dataEntrega) || '-'}</Text>
      {impostos ? <Text style={styles.linhaDiscreta}>{impostos}</Text> : null}
      {linha(f.observacaoItem) ? (
        <Text style={styles.linhaObservacao}>Obs.: {observacaoCurta(f.observacaoItem)}</Text>
      ) : null}
      {(pedido || contrato) ? (
        <Text style={styles.linhaDocumento}>{pedido ? `PC: ${pedido}  ` : ''}{contrato ? `CT: ${contrato}` : ''}</Text>
      ) : null}
      {!supplier.respondido && cotacaoTela ? <Text style={styles.naoRespondido}>Não respondido</Text> : null}
    </View>
  );
};

const LinhaProduto = ({ produto, grupo, indice, cotacaoTela }) => (
  <View style={[styles.linhaProduto, indice % 2 === 0 && styles.linhaAlternada]} wrap={false}>
    <View style={[styles.celulaConteudo, { width: COLUNA_PRODUTO }]}>
      <Text style={styles.codigoProduto}>{texto(produto.item)}  |  ({texto(produto.codigo)})</Text>
      <Text style={styles.descricaoProduto}>{texto(produto.nome)}</Text>
      {texto(produto.descricaoComplementar) ? <Text style={styles.produtoComplemento}>Compl.: {texto(produto.descricaoComplementar)}</Text> : null}
      {texto(produto.certificado) ? <Text style={styles.produtoComplemento}>C.A.: {texto(produto.certificado)}</Text> : null}
      {texto(produto.centroCusto) ? <Text style={styles.produtoComplemento}>CC: {texto(produto.centroCusto)}</Text> : null}
    </View>
    <View style={[styles.celulaConteudo, { width: COLUNA_QTD }]}>
      <Text style={styles.quantidade}>{texto(produto.quantidade)} {texto(produto.unidade)}</Text>
      <Text style={styles.miniLabel}>NECESSIDADE ENTREGA</Text>
      <Text style={styles.dataNecessidade}>{dataFormatada(produto.dataPrevisao) || '-'}</Text>
      {texto(produto.periodo) ? <Text style={styles.linhaDiscreta}>{texto(produto.periodo)} {texto(produto.tempo)}</Text> : null}
    </View>
    {grupo.map((supplier) => (
      <View style={[styles.ofertaContainer, { width: COLUNA_FORNECEDOR },
        cotacaoTela && ofertaSelecionada(produto, supplier) && styles.celulaSelecionada]} key={supplier.chave}>
        <DadosOferta supplier={supplier} produto={produto} cotacaoTela={cotacaoTela} />
      </View>
    ))}
    {grupo.length < 2 && Array.from({ length: 2 - grupo.length }, (_, index) => (
      <View key={`vazio-${index}`} style={[styles.celulaConteudo, { width: COLUNA_FORNECEDOR }]} />
    ))}
  </View>
);

// Resumo comercial centralizado: todos os fornecedores filtrados, uma única vez.
const ResumoFornecedores = ({ suppliers }) => (
  <View style={styles.resumoTabela}>
    <View style={styles.resumoTabelaCabecalho} wrap={false}>
      <View style={[styles.resumoCabecalhoCelula, { width: '33%' }]}>
        <Text>FORNECEDOR / CNPJ</Text>
      </View>
      <View style={[styles.resumoCabecalhoCelula, { width: '15%' }]}>
        <Text>TOTAL DA PROPOSTA</Text>
      </View>
      <View style={[styles.resumoCabecalhoCelula, { width: '17%' }]}>
        <Text>FRETE</Text>
      </View>
      <View style={[styles.resumoCabecalhoCelula, { width: '23%' }]}>
        <Text>CONDIÇÃO DE PAGAMENTO</Text>
      </View>
      <View style={[styles.resumoCabecalhoCelula, { width: '12%' }]}>
        <Text>VALIDADE</Text>
      </View>
    </View>
    {suppliers.map((fornecedor, idx) => (
      <View key={fornecedor.chave} wrap={false}
        style={[styles.resumoTabelaLinha, idx % 2 === 0 && styles.resumoTabelaLinhaAlternada]}>
        <View style={[styles.resumoCelula, { width: '33%' }]}>
          <Text style={styles.resumoNome}>{fornecedor.nome || '-'}</Text>
          <Text style={styles.resumoCnpj}>{fornecedor.cnpj || '-'}</Text>
        </View>
        <View style={[styles.resumoCelula, { width: '15%' }]}>
          <Text style={styles.resumoTotal}>{moeda(fornecedor.totalFornecedor)}</Text>
        </View>
        <View style={[styles.resumoCelula, { width: '17%' }]}>
          <Text>{tipoFrete(fornecedor)} - {moeda(fornecedor.frete)}</Text>
        </View>
        <View style={[styles.resumoCelula, { width: '23%' }]}>
          <Text>{valorOpcao(fornecedor.condicaoPagamento) || '-'}</Text>
        </View>
        <View style={[styles.resumoCelula, { width: '12%' }]}>
          <Text>{dataFormatada(fornecedor.dataValidade) || '-'}</Text>
        </View>
      </View>
    ))}
    {suppliers.length === 0 && (
      <View style={styles.resumoTabelaLinha}>
        <Text style={styles.resumoVazio}>Nenhum fornecedor encontrado no filtro.</Text>
      </View>
    )}
  </View>
);

const PaginaResumo = ({ suppliers, primeiro, empresa, dataHora, cotacaoTela }) => (
  <Page size="A4" orientation="landscape" style={styles.page}>
    <Cabecalho primeiro={primeiro} empresa={empresa} dataHora={dataHora} cotacaoTela={cotacaoTela} />
    <View style={styles.aberturaGrupo}>
      <Text style={styles.tituloSecao}>RESUMO COMERCIAL DOS FORNECEDORES</Text>
      <Text style={styles.contadorSecao}>{suppliers.length} fornecedor(es) exibido(s) no filtro</Text>
    </View>
    <ResumoFornecedores suppliers={suppliers} />
    <Text style={styles.resumoLegenda}>
      Comparativo consolidado. Observações e justificativas completas constam nas páginas anteriores.
    </Text>
    <Rodape />
  </Page>
);

// Divide textos longos para que o anexo também tenha cabeçalho, rodapé e quebras previsíveis.
function dividirEmLinhas(valor, largura = 115) {
  const linhas = [];
  linha(valor).split('\n').forEach((original) => {
    const palavras = original.split(/\s+/).filter(Boolean);
    if (!palavras.length) { linhas.push(''); return; }
    let atual = '';
    palavras.forEach((palavra) => {
      // Quebra mesmo palavras/códigos muito extensos (URLs, números etc.).
      const partes = palavra.match(new RegExp(`.{1,${largura}}`, 'g')) || [''];
      partes.forEach((parte) => {
        if (atual && atual.length + 1 + parte.length > largura) {
          linhas.push(atual);
          atual = parte;
        } else atual = atual ? `${atual} ${parte}` : parte;
      });
    });
    if (atual) linhas.push(atual);
  });
  return linhas;
}

function paginasDeDetalhes(suppliers, produtos) {
  const paginas = [];
  suppliers.forEach((supplier) => {
    const secoes = [
      ['Observações de pagamento', supplier.observacaoFornecedor],
      ['Observação do fornecedor no mapa', supplier.observacaoMapa],
      ['Justificativa', supplier.justificativa],
    ];
    produtos.forEach((produto) => {
      const f = encontrarResposta(produto, supplier);
      const obs = texto(f?.observacaoItem);
      if (obs.length > RESUMO_OBSERVACAO) {
        secoes.push([`Item ${texto(produto.item)} - ${texto(produto.codigo)} (observação completa)`, obs]);
      }
    });

    let pagina = [];
    let linhasRestantes = MAX_LINHAS_ANEXO;
    const guardarPagina = () => {
      if (pagina.length) paginas.push({ supplier, secoes: pagina });
      pagina = [];
      linhasRestantes = MAX_LINHAS_ANEXO;
    };

    secoes.forEach(([titulo, valor]) => {
      if (!texto(valor)) return;
      const linhas = dividirEmLinhas(valor);
      let offset = 0;
      while (offset < linhas.length) {
        if (linhasRestantes < 5) guardarPagina();
        const blocos = Math.min(linhas.length - offset, linhasRestantes - 2);
        if (blocos <= 0) { guardarPagina(); continue; }
        pagina.push({
          titulo: offset === 0 ? titulo : `${titulo} (continuação)`,
          conteudo: linhas.slice(offset, offset + blocos).join('\n'),
        });
        offset += blocos;
        linhasRestantes -= blocos + 2;
      }
    });
    guardarPagina();
  });
  return paginas;
}

const PaginaDetalhes = ({ pagina, primeiro, empresa, dataHora, cotacaoTela }) => (
  <Page size="A4" orientation="landscape" style={styles.page}>
    <Cabecalho primeiro={primeiro} empresa={empresa} dataHora={dataHora} cotacaoTela={cotacaoTela} />
    <View style={styles.aberturaGrupo}>
      <Text style={styles.tituloSecao}>DETALHAMENTO / OBSERVAÇÕES</Text>
      <Text style={styles.contadorSecao}>Informações complementares da proposta</Text>
    </View>
    <View style={styles.blocoDetalhe}>
      <Text style={styles.detalheFornecedor}>{pagina.supplier.nome}</Text>
      <Text style={styles.detalheCnpj}>{pagina.supplier.cnpj}</Text>
      {pagina.secoes.map((secao, idx) => (
        <View key={idx} style={styles.detalheSecao} wrap={false}>
          <Text style={styles.detalheTitulo}>{secao.titulo}</Text>
          <Text style={styles.detalheTexto}>{secao.conteudo}</Text>
        </View>
      ))}
    </View>
    <Rodape />
  </Page>
);

async function MapaCotacaoPDF(arrayMapa, empresaFilial, CotacaoTela, exibeMapa) {
  const produtos = Array.isArray(arrayMapa) ? arrayMapa : [];
  if (!produtos.length) throw new Error('Não há itens para imprimir no mapa de cotação.');

  const empresa = (empresasData.empresas || []).find((e) => e.empresaFilial === empresaFilial);
  const suppliers = fornecedoresVisiveis(produtos, CotacaoTela, exibeMapa);
  const grupos = chunks(suppliers, FORNECEDORES_POR_GRUPO);
  const dataHora = new Date();
  const paginasComparativo = [];

  // O React-PDF não oferece divisão de colunas horizontais automaticamente.
  // Cada grupo de dois fornecedores repete Produto e Quantidade em todas as páginas.
  if (!grupos.length) grupos.push([]);
  grupos.forEach((grupo, indiceGrupo) => {
    const paginasProdutos = distribuirProdutos(produtos, grupo);
    paginasProdutos.forEach((itensPagina, indicePagina) => {
      paginasComparativo.push({
        grupo,
        indiceGrupo,
        itensPagina,
        indicePagina,
        primeiroItem: produtos.indexOf(itensPagina[0]) + 1,
        ultimoItem: produtos.indexOf(itensPagina[itensPagina.length - 1]) + 1,
      });
    });
  });

  const paginasDetalhes = paginasDeDetalhes(suppliers, produtos);
  const documento = (
    <Document title={`Mapa Comparativo de Cotação - ${texto(produtos[0].cotacao)}`}>
      {paginasComparativo.map((p, indice) => (
        <Page key={`comparativo-${indice}`} size="A4" orientation="landscape" style={styles.page}>
          <Cabecalho primeiro={produtos[0]} empresa={empresa} dataHora={dataHora} cotacaoTela={CotacaoTela} />
          <View style={styles.aberturaGrupo}>
            <Text style={styles.tituloSecao}>COMPARATIVO DE ITENS ({produtos.length})</Text>
            <Text style={styles.contadorSecao}>
              Fornecedores {p.indiceGrupo * FORNECEDORES_POR_GRUPO + 1}-{p.indiceGrupo * FORNECEDORES_POR_GRUPO + p.grupo.length} de {suppliers.length}  |  Itens {p.primeiroItem}-{p.ultimoItem} de {produtos.length}
            </Text>
          </View>
          <View style={styles.mapaTabela}>
            <CabecalhoTabela grupo={p.grupo} />
            {p.itensPagina.map((produto, idx) => (
              <LinhaProduto key={`${texto(produto.item)}-${idx}`} produto={produto} grupo={p.grupo}
                indice={p.primeiroItem + idx} cotacaoTela={CotacaoTela} />
            ))}
          </View>
          <Rodape />
        </Page>
      ))}
      {paginasDetalhes.map((pagina, idx) => (
        <PaginaDetalhes key={`detalhes-${idx}`} pagina={pagina} primeiro={produtos[0]}
          empresa={empresa} dataHora={dataHora} cotacaoTela={CotacaoTela} />
      ))}
      {/* Página única consolidada: sempre a última página do PDF. */}
      <PaginaResumo suppliers={suppliers} primeiro={produtos[0]}
        empresa={empresa} dataHora={dataHora} cotacaoTela={CotacaoTela} />
    </Document>
  );

  try {
    return await pdf(documento).toBlob();
  } catch (error) {
    console.error('Erro ao gerar PDF do mapa comparativo:', error);
    throw error;
  }
}

export default MapaCotacaoPDF;
